class AnnouncementScroll extends HTMLElement {
  connectedCallback() {
    this.disconnect();
    this.wrapper = this.closest('.shopify-section');
    if (!this.wrapper) return;
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    this.lastScrollY = this.readScrollY();
    this.directionStart = this.lastScrollY;
    this.direction = 0;
    this.flowStart = this.wrapper.getBoundingClientRect().top + this.lastScrollY;
    this.height = this.offsetHeight;
    this.active = true;
    this.pinned = this.lastScrollY > this.flowStart + this.height;
    this.hiddenFromScroll = this.pinned;
    this.wrapper.setAttribute('data-announcement-scroll', '');
    this.applyState();

    document.addEventListener('scroll', () => this.schedule(), { passive: true, signal });
    this.addEventListener('focusin', () => {
      this.pinned = this.readScrollY() > 2;
      this.hiddenFromScroll = false;
      this.applyState(-1);
    }, { signal });
    this.addEventListener('focusout', () => this.schedule(), { signal });
    this.resizeObserver = new ResizeObserver(() => {
      this.height = this.offsetHeight;
      this.applyState();
    });
    this.resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this.disconnect();
  }

  disconnect() {
    this.active = false;
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = null;
    if (this.wrapper) {
      this.wrapper.removeAttribute('data-announcement-scroll');
      this.wrapper.removeAttribute('data-announcement-pinned');
      this.wrapper.removeAttribute('data-announcement-hidden');
      this.wrapper.style.removeProperty('--announcement-top-offset');
      this.inert = false;
      this.removeAttribute('aria-hidden');
      this.updateOffsets();
    }
  }

  readScrollY() {
    const page = document.scrollingElement;
    const maximum = page ? Math.max(0, page.scrollHeight - page.clientHeight) : Infinity;
    return Math.min(maximum, Math.max(0, page?.scrollTop ?? window.scrollY ?? 0));
  }

  schedule() {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.updateScroll();
    });
  }

  updateScroll() {
    const scrollY = this.readScrollY();
    const delta = scrollY - this.lastScrollY;
    this.lastScrollY = scrollY;
    const direction = Math.sign(delta);
    if (direction && direction !== this.direction) {
      this.direction = direction;
      this.directionStart = scrollY - delta;
    }
    if (scrollY <= 2) {
      this.pinned = false;
      this.hiddenFromScroll = false;
      this.directionStart = scrollY;
      this.applyState(-1);
      return;
    }
    if (this.contains(document.activeElement)) {
      this.pinned = true;
      this.hiddenFromScroll = false;
    } else if (direction > 0 && scrollY <= this.flowStart + this.height) {
      this.pinned = false;
      this.hiddenFromScroll = false;
    } else if (direction && Math.abs(scrollY - this.directionStart) >= 6) {
      this.pinned = true;
      this.hiddenFromScroll = direction > 0;
    }
    this.applyState(direction);
  }

  applyState(direction = 0) {
    if (!this.active || !this.wrapper) return;
    this.wrapper.toggleAttribute('data-announcement-pinned', this.pinned);
    this.wrapper.toggleAttribute('data-announcement-hidden', this.hiddenFromScroll);
    this.inert = this.hiddenFromScroll;
    if (this.hiddenFromScroll) this.setAttribute('aria-hidden', 'true');
    else this.removeAttribute('aria-hidden');
    this.updateOffsets(direction);
  }

  updateOffsets(direction = 0) {
    let offset = 0;
    document.querySelectorAll('announcement-scroll').forEach((bar) => {
      if (!(bar instanceof AnnouncementScroll) || !bar.active || !bar.wrapper) return;
      bar.wrapper.style.setProperty('--announcement-top-offset', `${offset}px`);
      if (bar.pinned && !bar.hiddenFromScroll) offset += bar.height;
    });
    const value = `${offset}px`;
    if (document.body.style.getPropertyValue('--announcement-sticky-offset') === value) return;
    document.body.style.setProperty('--announcement-sticky-offset', value);
    document.dispatchEvent(new CustomEvent('announcement-scroll-change', {
      detail: { pinned: offset > 0, direction },
    }));
  }
}

if (!customElements.get('announcement-scroll')) {
  customElements.define('announcement-scroll', AnnouncementScroll);
}
